import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { useCreditConfig } from '../../../contexts/CreditConfigContext';
import { generateDownloadFilename } from '../../../utils/fileUtils';
import wanTextVideo from '../../../assets/video/wan_text_generate_video.mp4';
import wanImageVideo from '../../../assets/video/wan_image_generate_video.mp4';
import './WanGenerator.scss';

export default function WanGenerator() {
  const { t } = useI18n();
  const { creditConfig } = useCreditConfig();
  const [mode, setMode] = useState('text');
  const [prompt, setPrompt] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const inputRef = useRef(null);
  const [duration, setDuration] = useState('5s');
  const [resolution, setResolution] = useState('720p');
  const [ratio, setRatio] = useState('16:9');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [negPrompt, setNegPrompt] = useState('');
  const [seed, setSeed] = useState('');
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const canGenerate = mode === 'text' ? prompt.trim().length > 0 : !!imageFile;

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return;
    setImageFile(f);
    const url = URL.createObjectURL(f);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
  };

  const onReplace = (e) => {
    e.stopPropagation();
    inputRef.current && inputRef.current.click();
  };
  const onRemove = (e) => {
    e.stopPropagation();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setImageFile(null);
  };
  const openPreview = (e) => {
    e.stopPropagation();
    setShowPreview(true);
  };
  const closePreview = () => setShowPreview(false);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleGenerate = () => {
    if (!canGenerate) return;
    setIsGenerating(true);
    setResult(null);
    
    // Simulate generation
    setTimeout(() => {
      setResult({
        url: mode === 'text' ? wanTextVideo : wanImageVideo,
        id: Math.random().toString(36).substring(7)
      });
      setIsGenerating(false);
    }, 3000);
  };

  const handleDownload = async () => {
    if (!result?.url) return;
    try {
      const response = await fetch(result.url);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      const filename = generateDownloadFilename(result.id);
      a.download = `${filename}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error('Download failed:', e);
      const a = document.createElement('a');
      a.href = result.url;
      const filename = generateDownloadFilename(result.id);
      a.download = `${filename}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const vg = creditConfig?.videoGeneration;
  const credits = vg?.costStandard;

  return (
    <section className="wan-generator">
      <div className="wan-generator__grid">
        <div className="wan-generator__left">
          <div className="wan-tabs">
            <button className={mode === 'text' ? 'wan-tab active' : 'wan-tab'} onClick={() => setMode('text')}>{t('videoGeneration.wan.tabs.textToVideo')}</button>
            <button className={mode === 'image' ? 'wan-tab active' : 'wan-tab'} onClick={() => setMode('image')}>{t('videoGeneration.wan.tabs.imageToVideo')}</button>
          </div>

          {mode === 'image' && (
            <div className={imageFile ? 'wan-drop has-file' : 'wan-drop'}>
              <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFileChange} style={imageFile ? { display: 'none' } : undefined} />
              {imageFile ? (
                <div className="wan-drop__preview" onClick={openPreview}>
                  {previewUrl && <img src={previewUrl} alt="preview" className="wan-drop__img" />}
                  <div className="veo2-upload__actions">
                    <button className="veo2-action is-ok" onClick={onReplace}>↻</button>
                    <button className="veo2-action is-danger" onClick={onRemove}>×</button>
                  </div>
                </div>
              ) : (
                <div className="wan-drop__hint">
                  <div className="wan-drop__icon">⬆︎</div>
                  <div className="wan-drop__text">{t('videoGeneration.wan.upload.addImage')}<br/>{t('videoGeneration.wan.upload.formats')}</div>
                </div>
              )}
            </div>
          )}

          <div className="wan-field">
            <div className="wan-field__label">{t('videoGeneration.wan.prompt.label')}</div>
            <div className="wan-textarea">
              <textarea
                placeholder={t('videoGeneration.wan.prompt.placeholder')}
                maxLength={800}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
              <div className="wan-textarea__sub">
                <span>{t('videoGeneration.wan.prompt.sub')}</span>
                <span>{t('videoGeneration.wan.prompt.count', { count: prompt.length })}</span>
              </div>
            </div>
          </div>

          <div className="wan-group">
            <div className="wan-group__title">{t('videoGeneration.wan.groups.duration')}</div>
            <div className="wan-options">
              <button className={duration === '5s' ? 'wan-opt active green' : 'wan-opt green'} onClick={() => setDuration('5s')}>5s</button>
              <button className={duration === '10s' ? 'wan-opt active green' : 'wan-opt green'} onClick={() => setDuration('10s')}>10s</button>
            </div>
          </div>

          <div className="wan-group">
            <div className="wan-group__title">{t('videoGeneration.wan.groups.resolution')}</div>
            <div className="wan-options">
              <button className={resolution === '720p' ? 'wan-opt active green' : 'wan-opt green'} onClick={() => setResolution('720p')}>720p</button>
              <button className={resolution === '1080p' ? 'wan-opt active green' : 'wan-opt green'} onClick={() => setResolution('1080p')}>1080p</button>
            </div>
          </div>

          {mode === 'text' ? (
            <div className="wan-group">
              <div className="wan-group__title">{t('videoGeneration.wan.groups.aspect')}</div>
              <div className="wan-aspects">
                <button className={ratio === '16:9' ? 'wan-aspect active' : 'wan-aspect'} onClick={() => setRatio('16:9')}>16:9</button>
                <button className={ratio === '9:16' ? 'wan-aspect active' : 'wan-aspect'} onClick={() => setRatio('9:16')}>9:16</button>
                <button className={ratio === '1:1' ? 'wan-aspect active' : 'wan-aspect'} onClick={() => setRatio('1:1')}>1:1</button>
              </div>
            </div>
          ) : (
            <div className="wan-info purple">
              {t('videoGeneration.wan.groups.aspectInfo')}
            </div>
          )}

          <div className="wan-advanced">
            <button className="wan-advanced__toggle" onClick={() => setAdvancedOpen(!advancedOpen)}>
              <span>{t('videoGeneration.wan.advanced.title')}</span>
              <span className={advancedOpen ? 'chev up' : 'chev down'} />
            </button>
            {advancedOpen && (
              <div className="wan-advanced__content">
                <div className="wan-adv-card">
                  <div className="wan-adv-title">{t('videoGeneration.wan.advanced.negativePrompt')}</div>
                  <div className="wan-adv-textarea">
                    <textarea
                      placeholder={t('videoGeneration.wan.advanced.negativePlaceholder')}
                      value={negPrompt}
                      onChange={(e) => setNegPrompt(e.target.value)}
                    />
                  </div>
                  <div className="wan-adv-help">{t('videoGeneration.wan.advanced.negativeHelp')}</div>
                </div>
                <div className="wan-adv-card">
                  <div className="wan-adv-title">{t('videoGeneration.wan.advanced.seed')}</div>
                  <div className="wan-adv-input">
                    <input
                      type="text"
                      placeholder={t('videoGeneration.wan.advanced.seedPlaceholder')}
                      value={seed}
                      onChange={(e) => setSeed(e.target.value)}
                    />
                  </div>
                  <div className="wan-adv-help">{t('videoGeneration.wan.advanced.seedHelp')}</div>
                </div>
              </div>
            )}
          </div>

          {typeof credits === 'number' && (
            <div className="wan-credits">
              <div className="wan-credits__bar">
                <span>{t('videoGeneration.wan.credits.bar')}</span>
                <span className="wan-credits__value">{t('videoGeneration.wan.credits.value', { value: credits })}</span>
              </div>
            </div>
          )}

          <button 
            className={`wan-generate ${canGenerate ? 'active' : ''} ${isGenerating ? 'generating' : ''}`} 
            disabled={!canGenerate || isGenerating}
            onClick={handleGenerate}
          >
            {isGenerating ? t('aiToolTemplate.generating') : (typeof credits === 'number' ? t('videoGeneration.wan.actions.generate', { value: credits }) : t('aiworkbench.generation.startGeneration'))}
          </button>
        </div>

        <div className="wan-generator__right">
          <div className={`wan-preview ${result ? 'has-result' : ''}`}>
            {result ? (
              <div className="veo2-video">
                <video className="veo2-video__player" src={result.url} controls autoPlay loop muted playsInline />
                <div className="veo2-video__bar">
                  <div className="veo2-video__info">
                    <div className="veo2-video__label">Generated Video</div>
                    <div className="veo2-video__meta">5s • {resolution}</div>
                  </div>
                  <button className="veo2-video__download" onClick={handleDownload}>
                    Download
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="wan-preview__icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="5" width="18" height="14" rx="2" stroke="#16a34a" strokeWidth="1.5"/>
                    <circle cx="8" cy="12" r="2" stroke="#16a34a" strokeWidth="1.5"/>
                    <path d="M12 16l3-4 4 6" stroke="#16a34a" strokeWidth="1.5"/>
                  </svg>
                </div>
                <div className="wan-preview__title">{t('videoGeneration.wan.preview.title')}</div>
                <div className="wan-preview__desc">{t('videoGeneration.wan.preview.desc')}</div>
              </>
            )}
            {isGenerating && (
              <div className="veo2-preview__mask">
                <div className="veo2-loading">
                  <div className="veo2-spinner" />
                  <span>{t('aiToolTemplate.generating')}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {showPreview && previewUrl && (
          <div className="veo2-modal" onClick={closePreview}>
            <div className="veo2-modal__content" onClick={(e) => e.stopPropagation()}>
              <img src={previewUrl} alt="preview" className="veo2-modal__img" />
              <button className="veo2-modal__close" onClick={closePreview}>×</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
